import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function PortalResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/password/reset", {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/portal/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-grey-light flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-md w-full mx-auto px-5 py-20 text-center">
          <h1 className="h-serif text-3xl text-purple-dark mb-3">Missing reset token</h1>
          <p className="text-sm text-ink/70 mb-6">
            This link looks broken. Please request a new password reset from the
            portal login page.
          </p>
          <Link
            to="/portal/login"
            className="inline-block bg-purple text-white hover:bg-purple-dark px-6 py-3 rounded-sm font-bold text-sm"
            data-testid="reset-back-to-login"
          >
            Back to portal login
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grey-light flex flex-col">
      <SiteHeader />
      <div className="flex-1 max-w-md w-full mx-auto px-5 py-16">
        <div className="border border-grey bg-white p-10">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-purple text-white mb-6">
            {done ? <CheckCircle2 size={22} /> : <KeyRound size={20} />}
          </div>
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-3">
            Parent portal · password reset
          </div>
          <h1 className="h-serif text-3xl text-purple-dark mb-3 tracking-tight">
            {done ? "All set." : "Choose a new password"}
          </h1>

          {done ? (
            <p className="text-sm text-ink/80 leading-relaxed">
              Your password has been updated. Redirecting you to the sign-in
              page…
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  New password
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="border-grey focus:border-purple focus:ring-purple"
                  data-testid="reset-new-password"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  Confirm password
                </Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="border-grey focus:border-purple focus:ring-purple"
                  data-testid="reset-confirm-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-purple text-white hover:bg-purple-dark font-bold py-3 rounded-sm text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Update password
              </button>
            </form>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
